/**
 * Full-page Add / Edit Location — replaces the LocationsTab Add Location dialog.
 * Routes:
 *   /cases/:caseId/locations/new
 *   /cases/:caseId/locations/:locationId/edit
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, MapPin, Loader2, Search } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { LOCATION_TYPES } from '../../components/case/constants';

const TOKEN_KEY = 'nexus_token';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const ensureGoogleMapsLoaded = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (window.__nexusGMapsPromise) return window.__nexusGMapsPromise;
  window.__nexusGMapsPromise = new Promise((resolve) => {
    if (!GOOGLE_MAPS_API_KEY) { resolve(null); return; }
    const existing = document.querySelector('script[data-nexus-gmaps]');
    if (existing) { existing.addEventListener('load', () => resolve(window.google)); return; }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    s.async = true; s.defer = true;
    s.setAttribute('data-nexus-gmaps', '1');
    s.onload = () => resolve(window.google);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return window.__nexusGMapsPromise;
};

const emptyForm = {
  name: '', location_type: 'OTHER', address: '', city: '', state: '', zip_code: '',
  latitude: '', longitude: '', description: '', notes: '',
};

export default function LocationFormPage() {
  const { caseId, locationId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const isEdit = !!locationId;

  const [caseInfo, setCaseInfo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [autocompleteReady, setAutocompleteReady] = useState(false);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setCaseInfo).catch(() => {});
  }, [caseId, token]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/cases/${caseId}/locations`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : [])
      .then(rows => {
        const loc = rows.find(x => x.id === locationId);
        if (!loc) {
          toast({ title: 'Location not found', variant: 'destructive' });
          navigate(`/cases/${caseId}?tab=locations`);
          return;
        }
        setForm({
          name: loc.name || '', location_type: loc.location_type || 'OTHER',
          address: loc.address || '', city: loc.city || '', state: loc.state || '',
          zip_code: loc.zip_code || '',
          latitude: loc.latitude != null ? String(loc.latitude) : '',
          longitude: loc.longitude != null ? String(loc.longitude) : '',
          description: loc.description || '', notes: loc.notes || '',
        });
      }).finally(() => setLoading(false));
  }, [caseId, locationId, token, isEdit, navigate]);

  useEffect(() => {
    let cancelled = false;
    ensureGoogleMapsLoaded().then((g) => {
      if (cancelled || !g || !addressInputRef.current) return;
      try {
        autocompleteRef.current = new g.maps.places.Autocomplete(addressInputRef.current, {
          fields: ['address_components', 'formatted_address', 'geometry', 'name'],
          types: ['geocode'],
          componentRestrictions: { country: 'us' },
        });
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (!place) return;
          const comps = place.address_components || [];
          const get = (type, short = false) => {
            const c = comps.find(x => x.types.includes(type));
            return c ? (short ? c.short_name : c.long_name) : '';
          };
          const street = [get('street_number'), get('route')].filter(Boolean).join(' ');
          const city = get('locality') || get('sublocality') || get('postal_town');
          const state = get('administrative_area_level_1', true);
          const zip = get('postal_code');
          const lat = place.geometry?.location?.lat?.();
          const lng = place.geometry?.location?.lng?.();
          setForm(prev => ({
            ...prev,
            address: street || place.formatted_address || prev.address,
            city: city || prev.city, state: state || prev.state, zip_code: zip || prev.zip_code,
            latitude: lat != null ? String(lat) : prev.latitude,
            longitude: lng != null ? String(lng) : prev.longitude,
            name: prev.name || place.name || '',
          }));
        });
        setAutocompleteReady(true);
      } catch { /* input still works as plain text */ }
    });
    return () => { cancelled = true; };
  }, []);

  const goBack = () => navigate(`/cases/${caseId}?tab=locations`);

  const submit = async () => {
    // Manual entry bypass: allow saving with just an address if Google Maps
    // doesn't return a match — auto-derive name from address/city.
    const derivedName = (form.name || '').trim()
      || (form.address || '').trim()
      || [form.city, form.state].filter(Boolean).join(', ');
    if (!derivedName) {
      toast({ title: 'Missing details', description: 'Provide a name or address to save this location.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: derivedName,
        latitude: form.latitude !== '' ? parseFloat(form.latitude) : null,
        longitude: form.longitude !== '' ? parseFloat(form.longitude) : null,
      };
      const url = isEdit
        ? `${BACKEND_URL}/api/locations/${locationId}`
        : `${BACKEND_URL}/api/cases/${caseId}/locations`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Save failed (${res.status})`);
      toast({ title: isEdit ? 'Location updated' : 'Location added', description: derivedName });
      goBack();
    } catch (e) {
      toast({ title: isEdit ? 'Could not update location' : 'Could not add location', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading location…
      </div>
    );
  }

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="location-form-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="location-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MapPin className="w-6 h-6 text-amber-300" /> {isEdit ? 'Edit Location' : 'Add Location'}
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-slate-300">Name</Label>
            <span className="text-[10px] text-slate-500">Optional — will use address if blank</span>
          </div>
          <Input value={form.name} onChange={e => setF('name', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white"
            placeholder="Subject's residence, surveillance point..."
            data-testid="location-name-input" autoComplete="off" />
        </div>
        <div>
          <Label className="text-slate-300">Type</Label>
          <Select value={form.location_type} onValueChange={v => setF('location_type', v)}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LOCATION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-slate-300">Address</Label>
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <Search className="w-3 h-3" /> {autocompleteReady ? 'Start typing to search' : 'Type an address'}
            </span>
          </div>
          <Input ref={addressInputRef} value={form.address} onChange={e => setF('address', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white"
            placeholder="e.g. 1600 Pennsylvania Ave NW, Washington"
            data-testid="location-address-input" autoComplete="off" />
          <p className="text-[11px] text-slate-400 mt-1">
            Pick a result to auto-fill city, state, ZIP and GPS coordinates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-slate-300">City</Label>
            <Input value={form.city} onChange={e => setF('city', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" placeholder="City" />
          </div>
          <div>
            <Label className="text-slate-300">State</Label>
            <Input value={form.state} onChange={e => setF('state', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" placeholder="State" />
          </div>
          <div>
            <Label className="text-slate-300">ZIP</Label>
            <Input value={form.zip_code} onChange={e => setF('zip_code', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" placeholder="ZIP" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300">Latitude</Label>
            <Input value={form.latitude} onChange={e => setF('latitude', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" placeholder="Auto-filled" />
          </div>
          <div>
            <Label className="text-slate-300">Longitude</Label>
            <Input value={form.longitude} onChange={e => setF('longitude', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" placeholder="Auto-filled" />
          </div>
        </div>

        <div>
          <Label className="text-slate-300">Description</Label>
          <Textarea value={form.description} onChange={e => setF('description', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={3}
            placeholder="Describe the location..." />
        </div>
        <div>
          <Label className="text-slate-300">Notes</Label>
          <Textarea value={form.notes} onChange={e => setF('notes', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={3}
            placeholder="Additional notes..." />
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="location-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="cancel-location-btn">
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-60"
          data-testid="submit-location-btn">
          <MapPin className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Location')}
        </Button>
      </div>
    </div>
  );
}
